import React, { useState } from 'react';
import { FormField } from './FormField';
import { SearchIcon, ShoppingBagIcon, PlusIcon, MinusIcon, XIcon, StoreIcon } from 'lucide-react';
import { MetadataItem } from './MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface Shop {
  id: number;
  name: string;
  description: string;
  logo: string;
}
interface Product {
  id: number;
  shopId: number;
  name: string;
  price: number;
  description: string;
  image: string;
  category: string;
}
interface CartItem {
  productId: number;
  quantity: number;
  product: Product;
}
interface ShopProductsPaymentProps {
  onExpiryDateChange?: (date: string) => void;
}
export function ShopProductsPayment({
  onExpiryDateChange
}: ShopProductsPaymentProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedShopId, setSelectedShopId] = useState<number | null>(1); // Default to first shop
  // Mock shops data
  const shops: Shop[] = [{
    id: 1,
    name: 'Smart Mobile Hub',
    description: 'Latest smartphones and mobile accessories',
    logo: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  }, {
    id: 2,
    name: 'Tech Gadgets Pro',
    description: 'Premium laptops, tablets and smart devices',
    logo: 'https://images.unsplash.com/photo-1498049794561-7780e7231661?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  }, {
    id: 3,
    name: 'Smart Home Store',
    description: 'Kitchen appliances and smart home devices',
    logo: 'https://images.unsplash.com/photo-1558089687-f282ffcbc126?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80'
  }];
  // Mock products data
  const products: Product[] = [
  // Smart Mobile Hub products
  {
    id: 101,
    shopId: 1,
    name: 'iPhone 15 Pro',
    price: 999.99,
    description: 'Latest flagship smartphone with A17 Pro chip',
    image: 'https://images.unsplash.com/photo-1592286927505-b0c2e0a13e60?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Mobile Phones'
  }, {
    id: 102,
    shopId: 1,
    name: 'Samsung Galaxy S24',
    price: 899.99,
    description: 'Powerful Android flagship with AI features',
    image: 'https://images.unsplash.com/photo-1610945415295-d9bbf067e59c?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Mobile Phones'
  }, {
    id: 103,
    shopId: 1,
    name: 'Wireless Charging Pad',
    price: 39.99,
    description: 'Fast wireless charger for all Qi-enabled devices',
    image: 'https://images.unsplash.com/photo-1591290619762-c588f7e8e86f?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Mobile Accessories'
  },
  // Tech Gadgets Pro products
  {
    id: 201,
    shopId: 2,
    name: 'MacBook Pro 16"',
    price: 2499.99,
    description: 'Professional laptop with M3 Pro chip',
    image: 'https://images.unsplash.com/photo-1517336714731-489689fd1ca8?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Laptops'
  }, {
    id: 202,
    shopId: 2,
    name: 'Apple Watch Ultra 2',
    price: 799.99,
    description: 'Advanced fitness and health tracking smartwatch',
    image: 'https://images.unsplash.com/photo-1579586337278-3befd40fd17a?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Smart Watches'
  }, {
    id: 203,
    shopId: 2,
    name: 'iPad Pro 12.9"',
    price: 1099.99,
    description: 'Powerful tablet with M2 chip and ProMotion display',
    image: 'https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Tablets'
  },
  // Smart Home Store products
  {
    id: 301,
    shopId: 3,
    name: 'Smart Coffee Maker',
    price: 199.99,
    description: 'WiFi-enabled coffee maker with app control',
    image: 'https://images.unsplash.com/photo-1517668808822-9ebb02f2a0e6?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Kitchen Appliances'
  }, {
    id: 302,
    shopId: 3,
    name: 'Robot Vacuum Cleaner',
    price: 349.99,
    description: 'Smart vacuum with mapping and auto-charging',
    image: 'https://images.unsplash.com/photo-1558317374-067fb5f30001?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Smart Home'
  }, {
    id: 303,
    shopId: 3,
    name: 'Air Fryer Pro',
    price: 129.99,
    description: 'Digital air fryer with 8 preset cooking modes',
    image: 'https://images.unsplash.com/photo-1585515320310-259814833e62?ixlib=rb-1.2.1&auto=format&fit=crop&w=100&q=80',
    category: 'Kitchen Appliances'
  }];
  // Filter products by selected shop and search term
  const filteredProducts = products.filter(product => (selectedShopId === null || product.shopId === selectedShopId) && (searchTerm === '' || product.name.toLowerCase().includes(searchTerm.toLowerCase()) || product.category.toLowerCase().includes(searchTerm.toLowerCase()) || product.description.toLowerCase().includes(searchTerm.toLowerCase())));
  const addToCart = (product: Product) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.productId === product.id);
      if (existingItem) {
        return prevCart.map(item => item.productId === product.id ? {
          ...item,
          quantity: item.quantity + 1
        } : item);
      } else {
        return [...prevCart, {
          productId: product.id,
          quantity: 1,
          product
        }];
      }
    });
  };
  const removeFromCart = (productId: number) => {
    setCart(prevCart => prevCart.filter(item => item.productId !== productId));
  };
  const updateQuantity = (productId: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    setCart(prevCart => prevCart.map(item => item.productId === productId ? {
      ...item,
      quantity: newQuantity
    } : item));
  };
  const totalAmount = cart.reduce((total, item) => total + item.product.price * item.quantity, 0);
  const selectedShop = shops.find(shop => shop.id === selectedShopId) || null;
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
    if (onExpiryDateChange) {
      onExpiryDateChange(date);
    }
  };
  return <div className="space-y-6" data-id="element-3568">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-3569">
        <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-3570">
          <StoreIcon size={18} className="mr-2 text-blue-600" data-id="element-3571" />
          Shop products payment
        </h2>
        <FormField label="Select shop" tooltip="Choose a shop to view their products" data-id="element-3572">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-3573">
            {shops.map(shop => <div key={shop.id} className={`border rounded-md p-3 cursor-pointer transition-colors ${selectedShopId === shop.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} onClick={() => setSelectedShopId(shop.id)} data-id="element-3574">
                <div className="flex items-center" data-id="element-3575">
                  <div className="w-12 h-12 rounded-md overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-3576">
                    <img src={shop.logo} alt={shop.name} className="w-full h-full object-cover" data-id="element-3577" />
                  </div>
                  <div className="ml-3" data-id="element-3578">
                    <h4 className="font-medium text-gray-800" data-id="element-3579">{shop.name}</h4>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-3580">
                      {shop.description}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </FormField>
      </div>
      {selectedShop && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-3581">
          <div className="flex items-center mb-4" data-id="element-3582">
            <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-100 mr-2" data-id="element-3583">
              <img src={selectedShop.logo} alt={selectedShop.name} className="w-full h-full object-cover" data-id="element-3584" />
            </div>
            <h3 className="text-md font-medium text-gray-700" data-id="element-3585">
              {selectedShop.name} Products
            </h3>
          </div>
          <FormField label="Search products" tooltip={`Search for products in ${selectedShop.name}`} data-id="element-3586">
            <div className="relative" data-id="element-3587">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3588">
                <SearchIcon size={18} className="text-gray-400" data-id="element-3589" />
              </div>
              <input type="text" className="block w-full pl-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search by name, category, or description" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-3590" />
            </div>
          </FormField>
          <div className="mt-4 mb-6" data-id="element-3591">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-3592">
              {filteredProducts.length > 0 ? filteredProducts.map(product => <div key={product.id} className="border border-gray-200 rounded-md p-3 hover:border-blue-300 transition-colors" data-id="element-3593">
                    <div className="flex" data-id="element-3594">
                      <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0 bg-gray-100" data-id="element-3595">
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-3596" />
                      </div>
                      <div className="ml-3 flex-1" data-id="element-3597">
                        <div className="flex justify-between" data-id="element-3598">
                          <h4 className="font-medium text-gray-800" data-id="element-3599">
                            {product.name}
                          </h4>
                          <span className="text-gray-700" data-id="element-3600">
                            RM {product.price.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-3601">
                          {product.description}
                        </p>
                        <div className="mt-2 flex justify-between items-center" data-id="element-3602">
                          <span className="text-xs text-gray-500" data-id="element-3603">
                            {product.category}
                          </span>
                          <button type="button" onClick={() => addToCart(product)} className="px-3 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md text-xs font-medium flex items-center" data-id="element-3604">
                            <PlusIcon size={12} className="mr-1" data-id="element-3605" />
                            Add to cart
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>) : <div className="col-span-2 text-center py-8 bg-gray-50 rounded-md" data-id="element-3606">
                  <SearchIcon size={24} className="mx-auto text-gray-400 mb-2" data-id="element-3607" />
                  <p className="text-gray-500" data-id="element-3608">No products found</p>
                  <p className="text-sm text-gray-400 mt-1" data-id="element-3609">
                    Try a different search term
                  </p>
                </div>}
            </div>
          </div>
        </div>}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-3610">
        <div className="flex justify-between items-center mb-3" data-id="element-3611">
          <h3 className="text-sm font-medium text-gray-700" data-id="element-3612">
            Selected products
          </h3>
          <div className="text-sm font-medium text-gray-800" data-id="element-3613">
            Total: RM {totalAmount.toFixed(2)}
          </div>
        </div>
        {cart.length > 0 ? <div className="border border-gray-200 rounded-md divide-y divide-gray-200" data-id="element-3614">
            {cart.map(item => <div key={item.productId} className="p-3 flex items-center" data-id="element-3615">
                <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0 bg-gray-100" data-id="element-3616">
                  <img src={item.product.image} alt={item.product.name} className="w-full h-full object-cover" data-id="element-3617" />
                </div>
                <div className="ml-3 flex-1" data-id="element-3618">
                  <div className="flex justify-between" data-id="element-3619">
                    <h4 className="font-medium text-gray-800" data-id="element-3620">
                      {item.product.name}
                    </h4>
                    <button type="button" onClick={() => removeFromCart(item.productId)} className="text-gray-400 hover:text-red-500" data-id="element-3621">
                      <XIcon size={16} data-id="element-3622" />
                    </button>
                  </div>
                  <div className="flex justify-between items-center mt-1" data-id="element-3623">
                    <div className="flex items-center border border-gray-200 rounded-md" data-id="element-3624">
                      <button type="button" onClick={() => updateQuantity(item.productId, item.quantity - 1)} className="px-2 py-1 text-gray-500 hover:text-gray-700" data-id="element-3625">
                        <MinusIcon size={14} data-id="element-3626" />
                      </button>
                      <span className="px-2 text-sm" data-id="element-3627">{item.quantity}</span>
                      <button type="button" onClick={() => updateQuantity(item.productId, item.quantity + 1)} className="px-2 py-1 text-gray-500 hover:text-gray-700" data-id="element-3628">
                        <PlusIcon size={14} data-id="element-3629" />
                      </button>
                    </div>
                    <span className="text-sm text-gray-700" data-id="element-3630">
                      RM {(item.product.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>)}
          </div> : <div className="text-center py-6 bg-gray-50 rounded-md border border-gray-200" data-id="element-3631">
            <ShoppingBagIcon size={24} className="mx-auto text-gray-400 mb-2" data-id="element-3632" />
            <p className="text-gray-500" data-id="element-3633">No products selected</p>
            <p className="text-sm text-gray-400 mt-1" data-id="element-3634">
              Select products from the shop above
            </p>
          </div>}
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-3635">
        <FormField label="Customer reference" tooltip="Reference for your customer" data-id="element-3636">
          <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Reference ID" data-id="element-3637" />
        </FormField>
      </div>
      <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} data-id="element-3638" />
    </div>;
}